import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { delocalizePathname, localizePath } from "@/routes/localizedPath";
import { getRouteMeta } from "../../scripts/prerender-routes";



export interface ProductJsonLd {
  name: string;
  description: string;
  image?: string;
  category?: string;
}

export interface FaqJsonLd {
  question: string;
  answer: string;
}

interface PageMetaProps {
  title: string;
  description: string;
  ogImage?: string;
  /** Optional alt text describing the OG/Twitter image for accessibility & richer previews */
  ogImageAlt?: string;
  /** Optional Product structured data for product pages */
  product?: ProductJsonLd;
  /** Optional FAQ structured data for FAQ pages */
  faqs?: FaqJsonLd[];
}

const SITE_NAME = "Kočevar d.o.o.";
const SITE_ORIGIN = "https://kocevar.eu";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.jpg`;

const ORGANIZATION_JSONLD = {
  "@context": "https://schema.org",
  "@type": ["Organization", "Manufacturer"],
  name: "Kočevar d.o.o.",
  legalName: "Kočevar, d.o.o.",
  url: SITE_ORIGIN,
  logo: `${SITE_ORIGIN}/logos/expert-trafo.svg`,
  image: `${SITE_ORIGIN}/og-image.jpg`,
  description:
    "Slovenian manufacturer of industrial resistance welding machines and equipment — spot welders, projection welders, seam welders, micro welding systems and custom automated welding lines.",
  foundingDate: "1972",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Sončna cesta 10",
    addressLocality: "Ločica ob Savinji",
    addressRegion: "Polzela",
    postalCode: "3313",
    addressCountry: "SI",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "sales",
      telephone: "+386-3-703-22-50",
      email: "info@kocevar.eu",
      areaServed: ["SI", "EU"],
      availableLanguage: ["Slovenian", "English", "German"],
    },
    {
      "@type": "ContactPoint",
      contactType: "technical support",
      telephone: "+386-3-703-22-50",
      email: "info@kocevar.eu",
      areaServed: ["SI", "EU"],
      availableLanguage: ["Slovenian", "English", "German"],
    },
  ],
  sameAs: [
    "https://www.linkedin.com/company/kocevar",
    "https://www.youtube.com/@kocevar",
  ],
};

const JSONLD_ORG_ID = "jsonld-organization";
const JSONLD_PRODUCT_ID = "jsonld-product";
const JSONLD_BREADCRUMB_ID = "jsonld-breadcrumb";
const JSONLD_FAQ_ID = "jsonld-faq";

const BREADCRUMB_LABELS: Record<string, string> = {
  products: "Products",
  solutions: "Solutions",
  services: "Services",
  "spare-parts": "Spare Parts & Components",
  consumables: "Consumables & Electrodes",
  accessories: "Accessories",
  "knowledge-hub": "Knowledge Hub",
  faq: "FAQ",
  testimonials: "Testimonials",
  "users-clients": "Users & Clients",
  partners: "Partners",
  "about-us": "About Us",
  "splosni-pogoji": "Privacy Policy",
};

function buildBreadcrumbJsonLd(pathname: string, pageTitle: string) {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return null;

  const items: { "@type": string; position: number; name: string; item?: string }[] = [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN },
  ];

  let path = "";
  for (let i = 0; i < segments.length; i++) {
    path += `/${segments[i]}`;
    const isLast = i === segments.length - 1;
    const label = isLast
      ? pageTitle
      : BREADCRUMB_LABELS[segments[i]] || segments[i].replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

    const entry: { "@type": string; position: number; name: string; item?: string } = {
      "@type": "ListItem",
      position: i + 2,
      name: label,
    };
    if (!isLast) entry.item = `${SITE_ORIGIN}${path}`;
    items.push(entry);
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

function setJsonLd(id: string, data: object) {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id: string) {
  document.getElementById(id)?.remove();
}

/**
 * Sets document title, meta description, Open Graph tags, canonical link,
 * and JSON-LD structured data (Organization + optional Product).
 */
const PageMeta = ({ title, description, ogImage, ogImageAlt, product, faqs }: PageMetaProps) => {
  const { pathname } = useLocation();
  const { lang } = useLanguage();

  useEffect(() => {
    // Look up bilingual registry entry for this path. If found and lang is SLO,
    // override props with the Slovenian title/description so per-route SLO meta
    // matches what the prerender plugin ships in /sl/... static HTML files.
    const delocalizedLookup = delocalizePathname(pathname);
    const entry = delocalizedLookup ? getRouteMeta(delocalizedLookup.enPath) : undefined;
    const effectiveTitle = lang === "sl" && entry?.titleSl ? entry.titleSl : title;
    const effectiveDescription = lang === "sl" && entry?.descriptionSl ? entry.descriptionSl : description;

    const fullTitle = `${effectiveTitle} | ${SITE_NAME}`;
    document.title = fullTitle;
    // Reflect current language at the document level for assistive tech & crawlers
    document.documentElement.setAttribute("lang", lang === "sl" ? "sl" : "en");


    const setMeta = (selector: string, attr: string, value: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        const [attrName, attrVal] = selector
          .match(/\[([^=]+)="([^"]+)"\]/)!
          .slice(1);
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };

    // Canonical URL — derive the EN canonical path from the current pathname,
    // then build distinct, lang-specific URLs with translated slugs.
    const delocalized = delocalizePathname(pathname);
    const enPath = delocalized?.enPath ?? "/"; // canonical EN path (no lang prefix)
    const pathNoLang = enPath === "/" ? "" : enPath; // for breadcrumb
    const enUrl = `${SITE_ORIGIN}${localizePath(enPath, "en")}`;
    const slUrl = `${SITE_ORIGIN}${localizePath(enPath, "sl")}`;
    const canonicalUrl = lang === "sl" ? slUrl : enUrl;

    // Remove any stale canonical or hreflang links that we don't manage
    // (e.g. leftovers from static index.html or previous renders) to avoid duplicates.
    document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
    document
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((el) => {
        const id = el.id;
        if (id !== "hreflang-en" && id !== "hreflang-sl" && id !== "hreflang-x-default") {
          el.remove();
        }
      });

    const canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    canonical.setAttribute("href", canonicalUrl);
    document.head.appendChild(canonical);

    // hreflang alternates — distinct URLs per language
    const setHreflang = (hl: string, href: string) => {
      const id = `hreflang-${hl}`;
      let el = document.getElementById(id) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement("link");
        el.id = id;
        el.setAttribute("rel", "alternate");
        el.setAttribute("hreflang", hl);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    setHreflang("en", enUrl);
    setHreflang("sl", slUrl);
    setHreflang("x-default", enUrl);

    setMeta('meta[name="description"]', "content", effectiveDescription);
    setMeta('meta[property="og:title"]', "content", fullTitle);
    setMeta('meta[property="og:description"]', "content", effectiveDescription);
    setMeta('meta[property="og:image"]', "content", ogImage || DEFAULT_OG_IMAGE);
    setMeta('meta[property="og:image:alt"]', "content", ogImageAlt || effectiveTitle);
    setMeta('meta[property="og:url"]', "content", canonicalUrl);
    setMeta('meta[property="og:site_name"]', "content", SITE_NAME);
    setMeta('meta[property="og:locale"]', "content", lang === "sl" ? "sl_SI" : "en_US");
    setMeta('meta[property="og:locale:alternate"]', "content", lang === "sl" ? "en_US" : "sl_SI");
    setMeta('meta[property="og:type"]', "content", product ? "product" : "website");
    setMeta('meta[name="twitter:title"]', "content", fullTitle);
    setMeta('meta[name="twitter:description"]', "content", effectiveDescription);
    setMeta('meta[name="twitter:image"]', "content", ogImage || DEFAULT_OG_IMAGE);
    setMeta('meta[name="twitter:image:alt"]', "content", ogImageAlt || effectiveTitle);

    // JSON-LD: Organization (always present)
    setJsonLd(JSONLD_ORG_ID, ORGANIZATION_JSONLD);

    // JSON-LD: Product (only on product pages)
    if (product) {
      setJsonLd(JSONLD_PRODUCT_ID, {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        description: product.description,
        image: product.image ? `${SITE_ORIGIN}${product.image}` : undefined,
        category: product.category,
        url: canonicalUrl,
        brand: {
          "@type": "Brand",
          name: "Kočevar",
        },
        manufacturer: {
          "@type": "Organization",
          name: "Kočevar d.o.o.",
          url: SITE_ORIGIN,
        },
        // B2B custom-built machines are quoted on request. We emit an AggregateOffer
        // so Google's Product rich-result validator accepts the schema (it requires
        // one of offers/review/aggregateRating) without inventing a fake price.
        offers: {
          "@type": "AggregateOffer",
          url: canonicalUrl,
          priceCurrency: "EUR",
          lowPrice: "0",
          offerCount: 1,
          availability: "https://schema.org/InStock",
          businessFunction: "https://schema.org/Sell",
          seller: {
            "@type": "Organization",
            name: "Kočevar d.o.o.",
            url: SITE_ORIGIN,
          },
        },
      });
    } else {
      removeJsonLd(JSONLD_PRODUCT_ID);
    }

    // JSON-LD: BreadcrumbList (on all subpages) — strip lang segment so crawl trail starts from Home
    const breadcrumb = buildBreadcrumbJsonLd(pathNoLang || "/", effectiveTitle);
    if (breadcrumb) {
      setJsonLd(JSONLD_BREADCRUMB_ID, breadcrumb);
    } else {
      removeJsonLd(JSONLD_BREADCRUMB_ID);
    }

    // JSON-LD: FAQPage (only when faqs are provided)
    if (faqs && faqs.length > 0) {
      setJsonLd(JSONLD_FAQ_ID, {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        inLanguage: lang === "sl" ? "sl-SI" : "en-US",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: f.answer,
          },
        })),
      });
    } else {
      removeJsonLd(JSONLD_FAQ_ID);
    }

    return () => {
      document.title = SITE_NAME;
      removeJsonLd(JSONLD_PRODUCT_ID);
      removeJsonLd(JSONLD_BREADCRUMB_ID);
      removeJsonLd(JSONLD_FAQ_ID);
    };
  }, [title, description, ogImage, ogImageAlt, pathname, product, faqs, lang]);

  return null;
};

export default PageMeta;
